// Sorting the pieces of a compte rendu before sharing it outside its circle
// (#115). Four columns: everything starts in « To sort », and nothing can be
// sent until that column is empty — so nothing is forgotten (grilling of
// 26/09/2026).

export interface SummaryPiece {
  id: string
  kind: string
  section: string
  // The meeting step it comes from (absent for the header: summary, attendance)
  step?: { key: string; type: string }
  title: string
  body: string
  // The facts the server sets and nobody rewrites (#132): shown on the card,
  // never editable. Absent on older shares.
  meta?: SummaryPieceFacts
  // The tension a decision or a next step came out of
  parentId?: string
}

// What the card shows of `meta`: the server's `SummaryPieceMeta`, read loosely
export interface SummaryPieceFacts {
  kind: string
  weather?: string
  roleName?: string
  memberName?: string
  closed?: boolean
  born?: boolean
  done?: boolean
  status?: string
  isNew?: boolean
  others?: string[]
  policy?: string
  proposals?: { title: string; changes: unknown[] }[]
  present?: string[]
  absent?: string[]
}

export type ShareColumn = 'todo' | 'keep' | 'edit' | 'drop'

export const shareColumns: ShareColumn[] = ['todo', 'keep', 'edit', 'drop']

export interface ShareBoard {
  // Piece ids per column, in the order shown
  columns: Record<ShareColumn, string[]>
  // The text of the pieces sent « with adjustment », as rewritten
  edited: Record<string, string>
}

export function createBoard(pieces: SummaryPiece[]): ShareBoard {
  return {
    columns: { todo: pieces.map((p) => p.id), keep: [], edit: [], drop: [] },
    edited: {},
  }
}

// Only the id and the text count for sorting: the same board sorts the tickets
// of a release in the console (#77).
export interface SortablePiece {
  id: string
  body: string
}

export function movePiece(
  board: ShareBoard,
  pieces: SortablePiece[],
  pieceId: string,
  to: ShareColumn,
  index?: number
): ShareBoard {
  const columns = Object.fromEntries(
    shareColumns.map((column) => [
      column,
      board.columns[column].filter((id) => id !== pieceId),
    ])
  ) as ShareBoard['columns']
  const target = [...columns[to]]
  target.splice(index ?? target.length, 0, pieceId)
  columns[to] = target

  // Entering « with adjustment » starts from the original text, and a rewrite
  // is kept if the piece goes back and forth
  const edited = { ...board.edited }
  if (to === 'edit' && edited[pieceId] === undefined) {
    edited[pieceId] = pieces.find((p) => p.id === pieceId)?.body ?? ''
  }
  return { columns, edited }
}

// « Everything else as is »: to go faster when little has to be held back
export function keepAllRemaining(board: ShareBoard): ShareBoard {
  return {
    ...board,
    columns: {
      ...board.columns,
      todo: [],
      keep: [...board.columns.keep, ...board.columns.todo],
    },
  }
}

export function editPiece(
  board: ShareBoard,
  pieceId: string,
  body: string
): ShareBoard {
  return { ...board, edited: { ...board.edited, [pieceId]: body } }
}

export function canContinue(board: ShareBoard): boolean {
  return board.columns.todo.length === 0
}

// What is sent: kept and adjusted pieces, in the compte rendu's order
export function finalPieces(
  board: ShareBoard,
  pieces: SortablePiece[]
): { id: string; body: string }[] {
  const keep = new Set(board.columns.keep)
  const edit = new Set(board.columns.edit)
  return pieces
    .filter((p) => keep.has(p.id) || edit.has(p.id))
    .map((p) => ({
      id: p.id,
      body: edit.has(p.id) ? (board.edited[p.id] ?? p.body) : p.body,
    }))
}

// Steps chosen or dropped in step 1 (#115): the pieces of a dropped step leave
// the board, those of a step taken back return to « To sort ». What was
// already sorted stays sorted.
export function syncBoard(
  board: ShareBoard,
  visiblePieces: SummaryPiece[]
): ShareBoard {
  const visible = new Set(visiblePieces.map((p) => p.id))
  const columns = Object.fromEntries(
    shareColumns.map((column) => [
      column,
      board.columns[column].filter((id) => visible.has(id)),
    ])
  ) as ShareBoard['columns']
  const placed = new Set(shareColumns.flatMap((column) => columns[column]))
  columns.todo = [
    ...columns.todo,
    ...visiblePieces.filter((p) => !placed.has(p.id)).map((p) => p.id),
  ]
  return { ...board, columns }
}

// The steps of the compte rendu, in its order, to choose from in step 1
export function summarySteps(
  pieces: SummaryPiece[]
): { key: string; type: string; title: string }[] {
  const steps = new Map<string, { key: string; type: string; title: string }>()
  for (const piece of pieces) {
    if (piece.step && !steps.has(piece.step.key)) {
      steps.set(piece.step.key, { ...piece.step, title: piece.section })
    }
  }
  return [...steps.values()]
}
